import React, { useCallback, useState } from "react";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import Located from "@mapmycustomers/shared/types/base/Located";
import { Company, Person } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import CreateCompanyModal from "@app/component/createEditEntity/CreateCompanyModal";
import CreatePersonModal from "@app/component/createEditEntity/CreatePersonModal";
import useMapView from "@app/component/view/utils/useMapView";
import DropPinAnnotation from "@app/scene/map/components/tools/DropPinTool/components/DropPinAnnotation";
import DropPinMarker from "@app/scene/map/components/tools/DropPinTool/components/DropPinMarker";

interface Props {}

const DropPinTool: React.FC<Props> = () => {
  const { updateViewStateAndFetch, viewState } = useMapView();

  const [createCompanyVisible, showCreateCompany, hideCreateCompany] = useBoolean();
  const [companyInitialValues, setCompanyInitialValues] = useState<Partial<Company>>();
  const [createPersonVisible, showCreatePerson, hideCreatePerson] = useBoolean();
  const [personInitialValues, setPersonInitialValues] = useState<Partial<Person>>();

  const handleHideCreateCompany = useCallback(
    (company) => {
      if (company) {
        updateViewStateAndFetch({ request: {} });
      }
      hideCreateCompany();
    },
    [hideCreateCompany, updateViewStateAndFetch]
  );

  const handleHideCreatePerson = useCallback(
    (person) => {
      if (person) {
        updateViewStateAndFetch({ request: {} });
      }
      hideCreatePerson();
    },
    [hideCreatePerson, updateViewStateAndFetch]
  );

  const handleCreateCompany = useCallback(
    (location: Located | undefined) => {
      setCompanyInitialValues(location);
      showCreateCompany();
    },
    [setCompanyInitialValues, showCreateCompany]
  );

  const handleCreatePerson = useCallback(
    (location: Located | undefined) => {
      setPersonInitialValues(location);
      showCreatePerson();
    },
    [setPersonInitialValues, showCreatePerson]
  );

  const isVisible = viewState.tool === MapTool.DROP_PIN;

  return (
    <>
      {isVisible && (
        <>
          <DropPinMarker />
          <DropPinAnnotation
            onCreateCompany={handleCreateCompany}
            onCreatePerson={handleCreatePerson}
          />
        </>
      )}

      {createCompanyVisible && (
        <CreateCompanyModal
          initialValues={companyInitialValues}
          onHide={handleHideCreateCompany}
          showPostCreationModal
        />
      )}

      {createPersonVisible && (
        <CreatePersonModal
          initialValues={personInitialValues}
          onHide={handleHideCreatePerson}
          showPostCreationModal
        />
      )}
    </>
  );
};

export default DropPinTool;
